import { FC, useCallback } from 'react';
import {
  ActionButton,
  confirmationHeaders,
  DESCRIPTION_MAX_LENGTH,
  DetailLayout,
  EditLock,
  Form,
  FormAlerts,
  FormField,
  FormInput,
  GetApiErrorMessage,
  InfoIconUrl,
  NAME_MAX_LENGTH,
  ObjectHeader,
  ObjectType,
  Padding,
  Stack,
  SystemClassifierDto,
  SystemClassifierValidFormData,
  SystemClassifierValidFormDataSchema,
  useMutateSystemClassifier,
  useMutationErrors,
  useNavigationBlockers,
  useAlertContext,
  validationTranslations,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SystemClassifierForm.translations.json';
import styled from 'styled-components';
import { SystemClassifierBreadcrumbs } from './SystemClassifierBreadcrumbs';
import { SystemClassifierItemFormTable } from './SystemClassifierItemFormTable';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'system-classifier-form',
);
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type SystemClassifierFormProps = {
  id: number;
  defaultValues: SystemClassifierDto;
  onClickCancel: () => void;
  onSuccess?: (response: SystemClassifierDto) => void;
};

export const SystemClassifierForm: FC<SystemClassifierFormProps> = (props) => {
  const { t } = useTranslation();
  const { id, defaultValues, onClickCancel, onSuccess } = props;
  const { mutateAsync } = useMutateSystemClassifier();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const { clear: clearBlockers } = useNavigationBlockers();

  const handleFinish = useCallback(
    (values: SystemClassifierValidFormData, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then((responseData) => {
          if (responseData) {
            if (Object.hasOwn(responseData, 'id')) {
              clearBlockers();
              onSuccess?.(responseData);
              setSuccess();
            } else {
              throw new Error('Expecting "id" in response data.');
            }
          }
        })
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      id,
      clearBlockers,
      setSuccess,
      onSuccess,
      setMutationErrors,
    ],
  );

  return (
    <Form
      schema={SystemClassifierValidFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={mutationErrors}
      onSubmit={handleFinish}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.SYSTEM_CLASSIFIER_DETAILS}
        breadCrumb={<SystemClassifierBreadcrumbs data={defaultValues} />}
        side={{
          header: (
            <Padding>
              <ObjectHeader
                upperText={defaultValues.code}
                lowerText={defaultValues.name}
              />
            </Padding>
          ),
          content: (
            <Padding>
              <Stack size="md">
                <FormField
                  name="name"
                  label={t('name.label')}
                  maxLength={NAME_MAX_LENGTH}
                  required
                />
                <FormField
                  name={'userManaged'}
                  label={t('userManaged.label')}
                  preview={{
                    previewOnly: true,
                    render: (value) => (value ? t('yes') : t('no')),
                    style: { paddingLeft: 0 },
                  }}
                />
                <FormField name="description" label={t('description.label')}>
                  <FormInput
                    name="description"
                    rows={2}
                    autoSize
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </FormField>
              </Stack>
            </Padding>
          ),
          footer: (
            <EditLock
              id={defaultValues.id}
              entity={defaultValues}
              objectType={ObjectType.SystemClassifier}
              onTimeout={onClickCancel}
            />
          ),
        }}
        headerConfig={{
          actions: (
            <>
              <ActionButton type="abort" onClick={onClickCancel} />
              <ActionButton type="formSubmit" />
            </>
          ),
        }}
        alert={<FormAlerts />}
      >
        <ContentWrapper>
          <SystemClassifierItemFormTable
            isUserManaged={defaultValues.userManaged}
          />
        </ContentWrapper>
      </DetailLayout>
    </Form>
  );
};

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;
